/**
 * E98.F5 — os documentos de apoio do caso: leitura proposta e leitura confirmada pelo perito.
 *
 * Espelha `apps/api/types/documento_de_apoio.py` e `apps/api/runtime/documentos_de_apoio.py`
 * (há teste de paridade dos campos). O servidor PROPÕE a leitura — do texto do documento ou do
 * reconhecimento da imagem — e nada grava; quem grava é o `PUT` que o perito dispara depois de
 * conferir. O exame só lê a leitura confirmada.
 */
import axios, { type AxiosInstance } from 'axios';

import type { TipoDeApoio } from '@/lib/exame_eletronico_protocolo';

export const TIPOS_DE_APOIO: readonly TipoDeApoio[] = [
  'documento_identidade',
  'cnh',
  'passaporte',
  'identidade_de_estrangeiro',
  'cnh_verso',
  'nota_fiscal',
  'extrato_bancario',
  'fatura_de_cartao',
  'crlv',
  'foto_do_veiculo',
];

/** E101.F4 — os documentos do contrato que ganham leitura confirmada quando lidos por imagem. */
export const TIPOS_DO_CONTRATO_LIDOS = [
  'ccb',
  'cet',
  'cadastro',
  'termo_de_servico',
  'proposta_de_seguro',
] as const;
export type TipoDoContratoLido = (typeof TIPOS_DO_CONTRATO_LIDOS)[number];
export type TipoLido = TipoDeApoio | TipoDoContratoLido;

export function eDoContrato(tipo: TipoLido | null | undefined): tipo is TipoDoContratoLido {
  return (TIPOS_DO_CONTRATO_LIDOS as readonly unknown[]).includes(tipo);
}

export type OrigemDaLeitura = 'camada_de_texto' | 'leitura_optica' | 'digitada';

export interface LeituraDeIdentidadeData {
  tipo: 'documento_identidade' | 'cnh' | 'passaporte' | 'identidade_de_estrangeiro';
  nome?: string | null;
  cpf?: string | null;
  nascimento?: string | null;
  registro?: string | null;
  emitido_em?: string | null;
  valido_ate?: string | null;
  /** E106.F3 — a identidade ampliada contra a CCB. */
  filiacao?: string | null;
  documento_de_identidade?: string | null;
  documento_de_origem?: string | null;
  /** E109.F1 (S-08) — o verso do RG: órgão expedidor e naturalidade. */
  orgao_expedidor?: string | null;
  naturalidade?: string | null;
}

export interface LeituraDoVersoDaCnhData {
  tipo: 'cnh_verso';
  nome?: string | null;
  registro?: string | null;
  nascimento?: string | null;
  valido_ate?: string | null;
}

export interface LeituraDaNotaFiscalData {
  tipo: 'nota_fiscal';
  chave_de_acesso?: string | null;
  numero?: string | null;
  emitida_em?: string | null;
  emitente?: string | null;
  emitente_cnpj?: string | null;
  destinatario?: string | null;
  destinatario_documento?: string | null;
  valor_total?: string | null;
  chassi?: string | null;
  marca_modelo?: string | null;
  cor?: string | null;
  ano_fabricacao?: number | null;
  ano_modelo?: number | null;
  alienacao_fiduciaria_em_favor_de?: string | null;
}

export interface LeituraDoComprovanteData {
  tipo: 'fatura_de_cartao' | 'extrato_bancario';
  titular?: string | null;
  cpf?: string | null;
  endereco?: string | null;
  cep?: string | null;
  agencia?: string | null;
  conta?: string | null;
}

/** E101.F4 — o CRLV digital do veículo. */
export interface LeituraDoCrlvData {
  tipo: 'crlv';
  placa?: string | null;
  renavam?: string | null;
  chassi?: string | null;
  marca_modelo?: string | null;
  ano_fabricacao?: number | null;
  ano_modelo?: number | null;
  cor?: string | null;
  combustivel?: string | null;
  proprietario?: string | null;
  proprietario_documento?: string | null;
  local?: string | null;
  emitido_em?: string | null;
}

/** E101.F4 — o texto que a câmera sobrepõe à fotografia do veículo. */
export interface LeituraDaFotoDoVeiculoData {
  tipo: 'foto_do_veiculo';
  placa?: string | null;
  tirada_em?: string | null;
  hora?: string | null;
  local?: string | null;
}

/** E101.F4 — um documento do contrato lido por imagem. */
export interface LeituraDoDocumentoDoContratoData {
  tipo: TipoDoContratoLido;
  numero?: string | null;
  local?: string | null;
  emitido_em?: string | null;
  nome?: string | null;
  cpf?: string | null;
  marca?: string | null;
  versao?: string | null;
  ano_fabricacao?: number | null;
  ano_modelo?: number | null;
  chassi?: string | null;
  cor?: string | null;
  combustivel?: string | null;
  valor?: string | null;
  identificadores?: string[];
}

export type LeituraDeApoioData =
  | LeituraDeIdentidadeData
  | LeituraDoVersoDaCnhData
  | LeituraDaNotaFiscalData
  | LeituraDoComprovanteData
  | LeituraDoCrlvData
  | LeituraDaFotoDoVeiculoData
  | LeituraDoDocumentoDoContratoData;

export interface LeituraConfirmadaData {
  leitura: LeituraDeApoioData;
  origem: OrigemDaLeitura;
  confirmado_por?: string | null;
  confirmado_em: string;
  /** E118.F1 (GUS-127): gravada pelo sistema porque nome e CPF conferem com o cadastro. */
  corroborada?: boolean;
}

export interface DocumentoDeApoioData {
  documento_id: string;
  nome: string;
  mime_type: string;
  tipo?: TipoLido | null;
  leitura_confirmada?: LeituraConfirmadaData | null;
  concluido: boolean;
  /** E101.F4 — veio do dossiê em imagem; o documento do contrato tem o tipo fixo. */
  lido_por_imagem?: boolean;
  /** E111.F1 (S-08, N-14) — a imagem que o escritório tirou da análise. */
  exclusao_da_analise?: ExclusaoDaAnaliseData | null;
  /**
   * GUS-296 item 3 (E205.F4.C04): a imagem é a fotografia eleita da comparação facial — a selfie ou
   * o retrato do documento de identidade. Não é CCB nem pede leitura de apoio.
   */
  foto_do_caso?: 'selfie' | 'retrato' | null;
}

export interface ExclusaoDaAnaliseData {
  motivo: string;
  usuario_id: string;
  excluida_em: string;
}

export interface DocumentosDeApoioDoCasoData {
  case_id: string;
  /** O caso traz a CCB do kit de veículos; sem ela a seção não aparece (decisão do owner). */
  kit_de_veiculos: boolean;
  /** E106.F3 — o caso veio de um arquivo único do consignado: o RG e a CNH para a identidade. */
  consignado_do_arquivo_unico?: boolean;
  /** E109.F1 — o caso veio no pacote do banco, ou traz imagem solta (o verso do RG do pacote). */
  imagens_do_material?: boolean;
  documentos: DocumentoDeApoioData[];
}

export interface PropostaDeLeituraData {
  documento_id: string;
  tipo_sugerido?: TipoLido | null;
  origem: OrigemDaLeitura;
  linhas_lidas: number;
  leitura?: LeituraDeApoioData | null;
}

/** Como o campo se digita e como vai ao servidor. */
export type FormatoDoCampo = 'texto' | 'data' | 'ano' | 'reais' | 'horario' | 'lista';

export interface CampoDaLeitura {
  chave: string;
  formato: FormatoDoCampo;
}

const texto = (chave: string): CampoDaLeitura => ({ chave, formato: 'texto' });
const data = (chave: string): CampoDaLeitura => ({ chave, formato: 'data' });

const IDENTIDADE: readonly CampoDaLeitura[] = [
  texto('nome'),
  texto('cpf'),
  data('nascimento'),
  texto('registro'),
  data('emitido_em'),
  data('valido_ate'),
  texto('filiacao'),
  texto('documento_de_identidade'),
  texto('documento_de_origem'),
  texto('orgao_expedidor'),
  texto('naturalidade'),
];
/** E116.F1 — só os dados que se comparam com os documentos da contratação. */
const DE_COMPARACAO: readonly CampoDaLeitura[] = [
  texto('nome'),
  texto('cpf'),
  data('nascimento'),
  texto('registro'),
  data('valido_ate'),
];
const ano = (chave: string): CampoDaLeitura => ({ chave, formato: 'ano' });
/** E101.F4 — os documentos do contrato lidos por imagem: a mesma forma para os cinco. */
const CONTRATO: readonly CampoDaLeitura[] = [
  texto('numero'),
  texto('local'),
  data('emitido_em'),
  texto('nome'),
  texto('cpf'),
  texto('marca'),
  texto('versao'),
  ano('ano_fabricacao'),
  ano('ano_modelo'),
  texto('chassi'),
  texto('cor'),
  texto('combustivel'),
  { chave: 'valor', formato: 'reais' },
  { chave: 'identificadores', formato: 'lista' },
];
const COMPROVANTE: readonly CampoDaLeitura[] = [
  texto('titular'),
  texto('cpf'),
  texto('endereco'),
  texto('cep'),
  texto('agencia'),
  texto('conta'),
];

/** Os campos de cada tipo, na ordem do formulário (paridade com os contratos do servidor). */
export const CAMPOS_POR_TIPO: Record<TipoLido, readonly CampoDaLeitura[]> = {
  documento_identidade: IDENTIDADE,
  cnh: IDENTIDADE,
  passaporte: DE_COMPARACAO,
  identidade_de_estrangeiro: DE_COMPARACAO,
  cnh_verso: [texto('nome'), texto('registro'), data('nascimento'), data('valido_ate')],
  nota_fiscal: [
    texto('chave_de_acesso'),
    texto('numero'),
    data('emitida_em'),
    texto('emitente'),
    texto('emitente_cnpj'),
    texto('destinatario'),
    texto('destinatario_documento'),
    { chave: 'valor_total', formato: 'reais' },
    texto('chassi'),
    texto('marca_modelo'),
    texto('cor'),
    { chave: 'ano_fabricacao', formato: 'ano' },
    { chave: 'ano_modelo', formato: 'ano' },
    texto('alienacao_fiduciaria_em_favor_de'),
  ],
  extrato_bancario: COMPROVANTE,
  fatura_de_cartao: COMPROVANTE,
  crlv: [
    texto('placa'),
    texto('renavam'),
    texto('chassi'),
    texto('marca_modelo'),
    ano('ano_fabricacao'),
    ano('ano_modelo'),
    texto('cor'),
    texto('combustivel'),
    texto('proprietario'),
    texto('proprietario_documento'),
    texto('local'),
    data('emitido_em'),
  ],
  foto_do_veiculo: [
    texto('placa'),
    data('tirada_em'),
    { chave: 'hora', formato: 'horario' },
    texto('local'),
  ],
  ccb: CONTRATO,
  cet: CONTRATO,
  cadastro: CONTRATO,
  termo_de_servico: CONTRATO,
  proposta_de_seguro: CONTRATO,
};

export type ValoresDaLeitura = Record<string, string>;

function paraTela(valor: unknown, formato: FormatoDoCampo): string {
  if (valor === null || valor === undefined) return '';
  if (formato === 'reais') return String(valor).replace('.', ',');
  if (formato === 'lista') return Array.isArray(valor) ? valor.join(' ') : String(valor);
  return String(valor);
}

/** O que o formulário mostra: a leitura (proposta ou confirmada) na forma do tipo. */
export function valoresDaLeitura(
  tipo: TipoLido,
  leitura: LeituraDeApoioData | null | undefined,
): ValoresDaLeitura {
  const lida = (leitura ?? {}) as Record<string, unknown>;
  return Object.fromEntries(
    CAMPOS_POR_TIPO[tipo].map((c) => [c.chave, paraTela(lida[c.chave], c.formato)]),
  );
}

function paraServidor(valor: string, formato: FormatoDoCampo): string | number | string[] | null {
  const limpo = valor.trim();
  if (formato === 'lista') return limpo === '' ? [] : limpo.split(/[\s,;]+/);
  if (limpo === '') return null;
  if (formato === 'ano') return Number.parseInt(limpo, 10);
  if (formato === 'reais') return limpo.replace(/\./g, '').replace(',', '.');
  return limpo;
}

/** A leitura que vai no `PUT`: vazio é nulo, valor em reais com ponto decimal, ano inteiro. */
export function leituraDosValores(tipo: TipoLido, valores: ValoresDaLeitura): LeituraDeApoioData {
  const campos = CAMPOS_POR_TIPO[tipo].map((c) => [
    c.chave,
    paraServidor(valores[c.chave] ?? '', c.formato),
  ]);
  return { tipo, ...Object.fromEntries(campos) } as LeituraDeApoioData;
}

export type FalhaDoApoio =
  | 'concluido'
  | 'forma'
  | 'nao_e_de_apoio'
  | 'indisponivel'
  | 'foto_do_caso'
  | 'nao_e_imagem';

export class DocumentoDeApoioError extends Error {
  constructor(readonly falha: FalhaDoApoio) {
    super(falha);
    this.name = 'DocumentoDeApoioError';
  }
}

function falhaDe(err: unknown): DocumentoDeApoioError {
  if (!axios.isAxiosError(err) || err.response === undefined) {
    return new DocumentoDeApoioError('indisponivel');
  }
  const { status, data: corpo } = err.response;
  const detalhe = (corpo as { detail?: unknown } | undefined)?.detail;
  if (detalhe === 'EXCLUSAO_RECUSADA_FOTO_DO_CASO')
    return new DocumentoDeApoioError('foto_do_caso');
  if (detalhe === 'EXCLUSAO_RECUSADA_NAO_E_IMAGEM')
    return new DocumentoDeApoioError('nao_e_imagem');
  if (status === 409) return new DocumentoDeApoioError('concluido');
  if (detalhe === 'DOCUMENTO_NAO_E_DE_APOIO') return new DocumentoDeApoioError('nao_e_de_apoio');
  if (status === 422 && typeof detalhe !== 'string') return new DocumentoDeApoioError('forma');
  return new DocumentoDeApoioError('indisponivel');
}

function base(caseId: string): string {
  return `/casos/${caseId}/documentos-de-apoio`;
}

export async function listarDocumentosDeApoio(
  client: AxiosInstance,
  caseId: string,
): Promise<DocumentosDeApoioDoCasoData> {
  try {
    const { data: corpo } = await client.get<DocumentosDeApoioDoCasoData>(base(caseId));
    if (!Array.isArray(corpo?.documentos)) throw new DocumentoDeApoioError('indisponivel');
    return corpo;
  } catch (err) {
    throw err instanceof DocumentoDeApoioError ? err : falhaDe(err);
  }
}

/**
 * Tempo de espera da leitura proposta, e não o padrão de 15 s do cliente.
 *
 * A primeira leitura da imagem depois que o servidor sobe carrega o reconhecimento óptico e lê
 * com uma thread, uma leitura por vez. Com 15 s o navegador desistia enquanto o servidor ainda
 * lia, e o perito via falha de uma leitura que deu certo (F-E98.F5-01, a mesma armadilha da
 * E82.F5 no processamento inicial).
 */
export const LEITURA_PROPOSTA_TIMEOUT_MS = 120_000;

export async function proporLeitura(
  client: AxiosInstance,
  caseId: string,
  documentoId: string,
  tipo: TipoLido | null,
): Promise<PropostaDeLeituraData> {
  try {
    const { data: corpo } = await client.post<PropostaDeLeituraData>(
      `${base(caseId)}/${documentoId}/leitura`,
      undefined,
      tipo === null
        ? { timeout: LEITURA_PROPOSTA_TIMEOUT_MS }
        : { timeout: LEITURA_PROPOSTA_TIMEOUT_MS, params: { tipo } },
    );
    return corpo;
  } catch (err) {
    throw falhaDe(err);
  }
}

export async function confirmarLeitura(
  client: AxiosInstance,
  caseId: string,
  documentoId: string,
  corpo: { leitura: LeituraDeApoioData; origem: OrigemDaLeitura },
): Promise<DocumentoDeApoioData> {
  try {
    const { data: gravado } = await client.put<DocumentoDeApoioData>(
      `${base(caseId)}/${documentoId}`,
      corpo,
    );
    return gravado;
  } catch (err) {
    throw falhaDe(err);
  }
}

/** E111.F1 (S-08, N-14): tira a imagem da análise, com o motivo; o servidor grava quem e quando. */
export async function excluirDaAnalise(
  client: AxiosInstance,
  caseId: string,
  documentoId: string,
  motivo: string,
): Promise<DocumentoDeApoioData> {
  try {
    const { data: gravado } = await client.post<DocumentoDeApoioData>(
      `${base(caseId)}/${documentoId}/exclusao`,
      { motivo },
    );
    return gravado;
  } catch (err) {
    throw falhaDe(err);
  }
}

/** A imagem volta à análise (e, sendo de documento, volta a pedir a leitura). */
export async function desfazerExclusaoDaAnalise(
  client: AxiosInstance,
  caseId: string,
  documentoId: string,
): Promise<DocumentoDeApoioData> {
  try {
    const { data: gravado } = await client.delete<DocumentoDeApoioData>(
      `${base(caseId)}/${documentoId}/exclusao`,
    );
    return gravado;
  } catch (err) {
    throw falhaDe(err);
  }
}
